// src/components/themes/default/admin/analytics/ProductAnalyticsView.tsx
"use client";

import { useState, useMemo } from "react";
import {
  Boxes,
  DollarSign,
  Download,
  Search,
  CheckCircle2,
  AlertTriangle,
  Award,
  ShieldCheck,
  Star,
} from "lucide-react";

interface ProductAnalyticsProps {
  data: any;
  isLight: boolean;
  timeRange: "7d" | "30d" | "90d";
  onRefresh?: () => void;
}

export default function ProductAnalyticsView({
  data,
  isLight,
  timeRange,
}: ProductAnalyticsProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredScatterPoint, setHoveredScatterPoint] = useState<any | null>(null);
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);

  const demandVsStock = data?.ai_demand_vs_stock || [];
  const bestSellers = data?.best_sellers || [];
  const priceScatter = data?.price_scatter || [];
  const targetGauge = data?.target_gauge || {
    achieved_pct: 0,
    current_sales: 0,
    target_sales: 500000,
  };
  const reviewDist = data?.review_dist || [];
  const totalCatalogItems = data?.total_products_count || demandVsStock.length || 0;

  const productKpis = useMemo(() => {
    const criticalStockouts = demandVsStock.filter((p: any) => p.alert === "critical").length;
    const warningStockouts = demandVsStock.filter((p: any) => p.alert === "warning").length;
    const totalUnitsSold = bestSellers.reduce((acc: number, curr: any) => acc + (curr.sold || 0), 0);
    const topRevenue = bestSellers.reduce((acc: number, curr: any) => acc + (curr.revenue || 0), 0);

    const healthyStockRatio =
      totalCatalogItems > 0
        ? Math.round(((totalCatalogItems - (criticalStockouts + warningStockouts)) / totalCatalogItems) * 100)
        : 85;

    return {
      totalCatalogItems,
      criticalStockouts,
      warningStockouts,
      totalUnitsSold,
      topRevenue,
      healthyStockRatio,
    };
  }, [demandVsStock, bestSellers, totalCatalogItems]);

  const filteredBestSellers = useMemo(() => {
    return bestSellers.filter((p: any) => p.name?.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [bestSellers, searchTerm]);

  const handleExportCSV = () => {
    if (!bestSellers.length) return;
    const headers = ["Product Name", "Units Sold (30d)", "Realized Revenue (BDT)"];
    const rows = bestSellers.map((p: any) => [
      `"${p.name}"`,
      p.sold,
      p.revenue || 0,
    ]);

    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map((r: any) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `product-performance-analytics-${timeRange}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* KPI Ribbon */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          className={`p-5 rounded-3xl border-2 transition-all ${
            isLight
              ? "bg-white border-slate-300 text-slate-900 shadow-sm"
              : "bg-slate-900 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Tracked Catalog Items</span>
            <div className={`p-2 rounded-xl ${isLight ? "bg-blue-50 text-blue-600" : "bg-blue-500/10 text-blue-400"}`}>
              <Boxes className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono tracking-tight">
            {productKpis.totalCatalogItems} Items
          </div>
          <div className="flex items-center gap-1.5 pt-1 text-xs font-bold text-emerald-500">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Product & Inventory Model</span>
          </div>
        </div>

        <div
          className={`p-5 rounded-3xl border-2 transition-all ${
            isLight
              ? "bg-white border-slate-300 text-slate-900 shadow-sm"
              : "bg-slate-900 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Stockout Risk (&le;7d)</span>
            <div className={`p-2 rounded-xl ${isLight ? "bg-rose-50 text-rose-600" : "bg-rose-500/10 text-rose-400"}`}>
              <AlertTriangle className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono tracking-tight text-rose-600 dark:text-rose-400">
            {productKpis.criticalStockouts} Critical SKUs
          </div>
          <div className="text-xs text-amber-500 font-bold pt-1">
            + {productKpis.warningStockouts} items running low
          </div>
        </div>

        <div
          className={`p-5 rounded-3xl border-2 transition-all ${
            isLight
              ? "bg-white border-slate-300 text-slate-900 shadow-sm"
              : "bg-slate-900 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Top Movers Volume</span>
            <div className={`p-2 rounded-xl ${isLight ? "bg-emerald-50 text-emerald-600" : "bg-emerald-500/10 text-emerald-400"}`}>
              <Award className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono tracking-tight text-emerald-600 dark:text-emerald-400">
            {productKpis.totalUnitsSold.toLocaleString()} Units
          </div>
          <div className="text-xs text-slate-400 font-bold pt-1 font-mono">
            ৳{productKpis.topRevenue.toLocaleString()} volume revenue
          </div>
        </div>

        <div
          className={`p-5 rounded-3xl border-2 transition-all ${
            isLight
              ? "bg-white border-slate-300 text-slate-900 shadow-sm"
              : "bg-slate-900 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Inventory Health Index</span>
            <div className={`p-2 rounded-xl ${isLight ? "bg-purple-50 text-purple-600" : "bg-purple-500/10 text-purple-400"}`}>
              <ShieldCheck className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono tracking-tight text-purple-600 dark:text-purple-400">
            {productKpis.healthyStockRatio}% Healthy
          </div>
          <div className="text-xs text-emerald-500 font-bold pt-1">Optimal turnover balance</div>
        </div>
      </div>

      {/* Row 1: Stock vs 30-Day Demand & Gauge */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div
          className={`lg:col-span-7 p-6 rounded-3xl border-2 space-y-4 transition-all ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Physical Stock vs 30-Day Projected Demand
              </h3>
              <p className="text-xs text-slate-500 font-medium">Warehouse inventory vs demand units</p>
            </div>
            <div className="flex items-center gap-3 text-xs font-bold font-mono">
              <span className="flex items-center gap-1.5"><div className="w-3 h-3 bg-emerald-500 rounded-none" /> In Stock</span>
              <span className="flex items-center gap-1.5"><div className="w-3 h-3 bg-purple-500 rounded-none" /> 30d Sold</span>
            </div>
          </div>

          <div className="relative pt-4">
            <div className="overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-slate-700 select-none">
              <div className="h-64 flex items-end justify-start gap-2.5 px-2 min-w-max">
                {demandVsStock.map((p: any, idx: number) => {
                  const maxVal = Math.max(...demandVsStock.map((d: any) => Math.max(d.stock, d.demand_30d)), 10);
                  const stockHeight = Math.min(80, Math.max(6, (p.stock / maxVal) * 80));
                  const demandHeight = Math.min(80, Math.max(6, (p.demand_30d / maxVal) * 80));
                  const isHovered = hoveredBarIndex === idx;

                  return (
                    <div
                      key={idx}
                      onMouseEnter={() => setHoveredBarIndex(idx)}
                      onMouseLeave={() => setHoveredBarIndex(null)}
                      className="w-18 min-w-[68px] shrink-0 flex flex-col items-center justify-end h-full group relative cursor-pointer"
                    >
                      {isHovered && (
                        <div className="absolute -top-12 px-3 py-1.5 rounded-xl bg-black text-white text-[10px] font-mono font-bold pointer-events-none whitespace-nowrap z-30 shadow-xl animate-in fade-in">
                          <div>{p.name}</div>
                          <div className="text-emerald-400">Stock: {p.stock} pcs</div>
                          <div className="text-purple-400">Demand: {p.demand_30d} pcs</div>
                        </div>
                      )}

                      <div className="w-full flex items-end justify-center gap-1 h-52 pb-0.5">
                        <div className="flex flex-col items-center justify-end h-full w-7">
                          <span className="text-[10px] font-mono font-black mb-1 leading-none select-none text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                            {p.stock}
                          </span>
                          <div className="w-full bg-emerald-500 rounded-none transition-all duration-300" style={{ height: `${stockHeight}%` }} />
                        </div>
                        <div className="flex flex-col items-center justify-end h-full w-7">
                          <span className="text-[10px] font-mono font-black mb-1 leading-none select-none text-purple-600 dark:text-purple-400 whitespace-nowrap">
                            {p.demand_30d}
                          </span>
                          <div className="w-full bg-purple-500 rounded-none transition-all duration-300" style={{ height: `${demandHeight}%` }} />
                        </div>
                      </div>

                      <span className="text-[10px] font-bold text-slate-400 truncate w-full text-center mt-2 group-hover:text-white transition-colors">
                        {p.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={`border-b-2 mt-1 ${isLight ? "border-slate-300" : "border-slate-800"}`} />
          </div>
        </div>

        <div
          className={`lg:col-span-5 p-6 rounded-3xl border-2 space-y-4 transition-all flex flex-col justify-between ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Sales Goal Achievement Gauge
              </h3>
              <p className="text-xs text-slate-500 font-medium">Revenue target vs Realized total</p>
            </div>
            <span className="text-[10px] font-mono font-bold text-amber-500">KPI Gauge</span>
          </div>

          <div className="flex flex-col items-center justify-center py-4 space-y-3">
            <div className="relative w-56 h-32 flex items-center justify-center overflow-hidden">
              <svg viewBox="0 0 100 50" className="w-full h-full">
                <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke={isLight ? "#e2e8f0" : "#1e293b"} strokeWidth="11" />
                <path
                  d="M 10 50 A 40 40 0 0 1 90 50"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="11"
                  strokeDasharray="125.6"
                  strokeDashoffset={`${125.6 - (125.6 * Math.min(100, targetGauge.achieved_pct)) / 100}`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute bottom-0 text-center">
                <span className="text-3xl font-black font-mono text-amber-500">{targetGauge.achieved_pct}%</span>
                <span className="text-[10px] block text-slate-400 font-black uppercase tracking-wider">Target Realized</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 w-full max-w-sm pt-2 text-xs font-mono">
              <div className={`p-3 rounded-2xl border-2 ${isLight ? "bg-slate-50 border-slate-300" : "bg-slate-950 border-slate-800"}`}>
                <span className="text-[10px] text-slate-400 uppercase font-sans font-bold block">Current Sales</span>
                <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">৳{Number(targetGauge.current_sales || 0).toLocaleString()}</span>
              </div>
              <div className={`p-3 rounded-2xl border-2 text-right ${isLight ? "bg-slate-50 border-slate-300" : "bg-slate-950 border-slate-800"}`}>
                <span className="text-[10px] text-slate-400 uppercase font-sans font-bold block">Target</span>
                <span className="text-sm font-black">৳{Number(targetGauge.target_sales || 500000).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Price Scatter & Stockout Gauges */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div
          className={`lg:col-span-7 p-6 rounded-3xl border-2 space-y-4 transition-all ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Product Price বনাম Sales Units Scatter Plot
              </h3>
              <p className="text-xs text-slate-500 font-medium">X-Axis: Selling Price (৳) | Y-Axis: Units Sold</p>
            </div>
            <span className="text-[10px] font-mono font-bold text-rose-500">Price Elasticity</span>
          </div>

          <div className="h-64 relative w-full pt-3">
            <svg viewBox="0 0 450 180" className="w-full h-full overflow-visible">
              <line x1="30" y1="20" x2="30" y2="160" stroke={isLight ? "#cbd5e1" : "#334155"} strokeWidth="1.5" />
              <line x1="30" y1="160" x2="430" y2="160" stroke={isLight ? "#cbd5e1" : "#334155"} strokeWidth="1.5" />
              <line x1="30" y1="90" x2="430" y2="90" stroke={isLight ? "#f1f5f9" : "#1e293b"} strokeDasharray="3" />
              <line x1="30" y1="30" x2="430" y2="30" stroke={isLight ? "#f1f5f9" : "#1e293b"} strokeDasharray="3" />

              <text x="5" y="32" fill="#94a3b8" fontSize="8" fontFamily="monospace">High</text>
              <text x="5" y="92" fill="#94a3b8" fontSize="8" fontFamily="monospace">Mid</text>
              <text x="15" y="162" fill="#94a3b8" fontSize="8" fontFamily="monospace">0</text>

              {priceScatter.map((pt: any, idx: number) => {
                const isHovered = hoveredScatterPoint?.name === pt.name;
                return (
                  <g
                    key={idx}
                    onMouseEnter={() => setHoveredScatterPoint(pt)}
                    onMouseLeave={() => setHoveredScatterPoint(null)}
                    className="cursor-pointer"
                  >
                    <circle
                      cx={pt.x}
                      cy={pt.y}
                      r={isHovered ? pt.r + 3 : pt.r}
                      fill={pt.color}
                      stroke={isLight ? "#ffffff" : "#0f172a"}
                      strokeWidth="2"
                    />
                    <text x={pt.x - 20} y={pt.y - 12} fill={isLight ? "#0f172a" : "#ffffff"} fontSize="8" fontWeight="bold">
                      {pt.name}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        <div
          className={`lg:col-span-5 p-6 rounded-3xl border-2 space-y-4 transition-all flex flex-col justify-between ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Stockout Countdown (Days Left)
              </h3>
              <p className="text-xs text-slate-500 font-medium">Estimated runout timeframe based on burn rate</p>
            </div>
            <span className="text-[10px] font-mono font-bold text-rose-500">Runout Meter</span>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-1 max-h-56 overflow-y-auto pr-1 scrollbar-thin">
            {demandVsStock.map((item: any, idx: number) => {
              const isCrit = item.alert === "critical";
              const isWarn = item.alert === "warning";
              return (
                <div
                  key={idx}
                  className={`p-3 rounded-2xl border-2 space-y-1.5 flex flex-col justify-between ${
                    isCrit
                      ? isLight ? "bg-rose-50/80 border-rose-300 text-rose-950" : "bg-rose-950/20 border-rose-800/40 text-rose-200"
                      : isWarn
                      ? isLight ? "bg-amber-50/80 border-amber-300 text-amber-950" : "bg-amber-950/20 border-amber-800/40 text-amber-200"
                      : isLight ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-slate-950 border-slate-800 text-slate-300"
                  }`}
                >
                  <div className="font-bold text-xs truncate">{item.name}</div>
                  <div className="text-xl font-black font-mono">~{item.days_left || (isCrit ? 4 : isWarn ? 8 : 28)}d</div>
                  <div className="text-[9px] font-mono font-bold pt-1 border-t border-black/10 flex justify-between">
                    <span>Stk: {item.stock}</span>
                    <span>30d: {item.demand_30d}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Row 3: Reviews & Best Sellers Table */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div
          className={`lg:col-span-5 p-6 rounded-3xl border-2 space-y-4 transition-all flex flex-col justify-between ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Review Ratings vs Return Correlation
              </h3>
              <p className="text-xs text-slate-500 font-medium">Ratings distribution from Review model</p>
            </div>
            <span className="text-[10px] font-mono font-bold text-amber-500">Quality Index</span>
          </div>

          <div className="space-y-3 pt-1">
            {reviewDist.map((r: any, idx: number) => (
              <div
                key={idx}
                className={`p-3.5 rounded-2xl border-2 flex items-center justify-between ${
                  isLight ? "bg-slate-50 border-slate-300" : "bg-slate-950 border-slate-800"
                }`}
              >
                <div className="flex items-center gap-1.5 font-bold text-xs">
                  <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                  <span>{r.stars}</span>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-black font-mono ${r.color}`}>{r.returns}</span>
                  <span className="text-[10px] block text-slate-400">Return Likelihood</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className={`lg:col-span-7 p-6 rounded-3xl border-2 space-y-4 transition-all ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Top Best Selling Products
              </h3>
              <p className="text-xs text-slate-500 font-medium">Live aggregation from OrderItem</p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-1.5 rounded-xl border text-xs bg-transparent"
              />
              <button onClick={handleExportCSV} className="p-2 border rounded-xl hover:opacity-80">
                <Download className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="overflow-x-auto w-full">
            <table className="w-full text-left text-xs min-w-[550px]">
              <thead className="uppercase text-[10px] text-slate-400 border-b">
                <tr>
                  <th className="py-2.5 px-3">Rank</th>
                  <th className="py-2.5 px-3">Product Name</th>
                  <th className="py-2.5 px-3">Units Sold</th>
                  <th className="py-2.5 px-3">Revenue Realized</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/40">
                {filteredBestSellers.map((prod: any, idx: number) => (
                  <tr key={idx} className="hover:bg-slate-500/10">
                    <td className="py-2.5 px-3 font-mono font-bold">#{idx + 1}</td>
                    <td className="py-2.5 px-3 font-bold">{prod.name}</td>
                    <td className="py-2.5 px-3 font-mono text-emerald-500 font-black">{prod.sold} pcs</td>
                    <td className="py-2.5 px-3 font-mono font-bold">৳{Number(prod.revenue || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}